/**
 * Pricing configuration — mirrors `.local/notes/pricing-structure-v3.md`.
 * Single source of truth for tier prices, marketplace discounts, credit amounts,
 * promo codes, and integration bands.
 *
 * WHEN YOU CHANGE THIS FILE: also update pricing-structure-v3.md in workspace-main
 * and notify Brandon. Public pages render directly off these constants.
 */

export type TierId = 'free' | 'solo' | 'pro' | 'team' | 'enterprise';

export interface Tier {
  id: TierId;
  name: string;
  price_label: string;
  price_sub?: string;
  /** Struck-through "Intro" comparison price — what the tier will eventually
   * cost once intro pricing ends. Renders as `~~$150~~ Intro: $50/mo`. Only
   * set on solo/pro/team — free has no future price, enterprise is custom. */
  price_label_intro_struck?: string;
  /** Marketing label for the lifetime price-lock benefit shown next to the
   * Founding cohort counter. E.g. "Lock $50/mo for life". Solo/Pro/Team only. */
  founding_locked_label?: string;
  /** Optional small-print clarification (e.g. Team's per-seat caveat). */
  founding_lock_fine_print?: string;
  seats: number | string;
  summary: string;
  features: string[];
  included_credits_retail: number;
  marketplace_discount_pct: number;
  highlight?: boolean;
  cta: 'try_it_now' | 'waitlist' | 'contact';
}

/**
 * The free tier, as decided by Brandon (2026-09-21) and built 2026-09-26.
 * Mirrors the hub's enforcement numbers (ctrl-a_workspace-main
 * src/billing/free-tier-policy.ts). The hub is what actually meters and stops
 * usage; these values are what we PROMISE — tests/unit/pricing-truth.test.ts
 * pins them so the page cannot drift from the decision again (it said
 * "7 days · 40 messages · 2 images" while nothing enforced that).
 */
export const FREE_TIER = {
  days: 14,
  messages: 200,
  images: 5,
  videos: 1,
} as const;

export const TIERS: Tier[] = [
  {
    id: 'free',
    name: 'Free trial',
    price_label: '$0',
    price_sub: `${FREE_TIER.days} days · no card required`,
    seats: 1,
    summary: `Try CTRL-A for ${FREE_TIER.days} days with your starter agent team. We only ask for your email.`,
    // Exactly what the trial includes (gate M5, 2026-09-26) — nothing implied
    // beyond this list. Tier-2 tools (e.g. delegating between agents) and
    // non-MiniMax image models are NOT in the trial.
    features: [
      `${FREE_TIER.days} days, no card required`,
      `${FREE_TIER.messages} agent messages, including automations (scheduled tasks and workflows)`,
      `${FREE_TIER.images} AI images (MiniMax)`,
      `${FREE_TIER.videos} short AI video (MiniMax, 768p, 6 seconds)`,
      'Chat with your starter agent team',
      'Set business goals and track them',
      'Keep everything you create when the trial ends',
    ],
    included_credits_retail: 0,
    marketplace_discount_pct: 0,
    cta: 'try_it_now',
  },
  {
    id: 'solo',
    name: 'Solo',
    price_label: '$50',
    price_sub: 'per month',
    price_label_intro_struck: '$150',
    founding_locked_label: 'Lock $50/mo for life — first 100 only',
    seats: 1,
    summary: 'One Chief of Staff agent with background helpers. Perfect for solopreneurs who need an extra brain.',
    features: [
      '1 main agent (Chief of Staff)',
      'Background sub-agents as tools',
      '$5 in image/video credits / mo',
      'Full marketplace access — à la carte',
      'Hire agents and skills at baseline prices',
    ],
    included_credits_retail: 5,
    marketplace_discount_pct: 0,
    cta: 'waitlist',
  },
  {
    id: 'pro',
    name: 'Pro',
    price_label: '$350',
    price_sub: 'per month',
    price_label_intro_struck: '$1,050',
    founding_locked_label: 'Lock $350/mo for life — first 100 only',
    seats: 1,
    summary: 'Orchestration unlocked. Your Chief of Staff now runs a full team of specialists.',
    features: [
      'Everything in Solo',
      '5 base specialists included (email, research, scheduler, content, ops)',
      'Visible delegation + org chart',
      '$20 in credits / mo (~33 images OR 1 video)',
      '10% off all marketplace agents and skills',
    ],
    included_credits_retail: 20,
    marketplace_discount_pct: 10,
    highlight: true,
    cta: 'waitlist',
  },
  {
    id: 'team',
    name: 'Team',
    price_label: '$500',
    price_sub: 'for 2 seats + $185 / seat',
    price_label_intro_struck: '$1,500',
    founding_locked_label: 'Lock $500 base/mo for life — first 100 only',
    founding_lock_fine_print: 'First 100 Team accounts only. Per-seat additions above the 2 included seats follow current per-seat rate at time of addition — only the $500 base is locked.',
    seats: '2+',
    summary: 'Multi-seat org chart with personal agents per person, shared specialists, full coordination.',
    features: [
      'Everything in Pro',
      '2 seats included + $185 per additional seat',
      'Personal agent per person',
      'Rooms, trust levels, budgets, CEO Session',
      '$60 in credits / mo',
      '20% off all marketplace agents and skills',
    ],
    included_credits_retail: 60,
    marketplace_discount_pct: 20,
    cta: 'waitlist',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price_label: 'Custom',
    price_sub: 'Scoped to you',
    seats: 'custom',
    summary: 'Dedicated infrastructure options — private VPS, white-label UI, private LLMs, air-gapped deployments.',
    features: [
      'Everything in Team + priority',
      'Custom SLAs',
      'Private VPS / white-label UI / private main instance',
      'Private LLM (Gemma fine-tune) — custom quote',
      'Local grid / on-prem / air-gapped available',
      '30% off marketplace (or negotiated)',
    ],
    included_credits_retail: 0, // custom — not shown numerically
    marketplace_discount_pct: 30,
    cta: 'contact',
  },
];

/** Retail prices per generation. Our margins are in pricing-structure-v3.md. */
export const CREDIT_RETAIL = {
  image_dollars: 0.6,  // $0.60 per image
  video_dollars: 15,   // $15 per video
} as const;

/** Top-up purchase packs. Prepaid credit balance, does not expire. */
export const CREDIT_PACKS = [
  { label: 'Starter pack',   dollars: 10 },
  { label: 'Standard pack',  dollars: 25 },
  { label: 'Pro pack',       dollars: 50 },
  { label: 'Studio pack',    dollars: 100 },
] as const;

// ─────────────────────────────────────────────────────────────────────────
// Marketplace
// ─────────────────────────────────────────────────────────────────────────

export type IntegrationBand = 'free' | 'light' | 'standard' | 'deep' | 'marquee';

export const INTEGRATION_BANDS: Record<IntegrationBand, { label: string; monthly_t1: number | null; description: string }> = {
  free:     { label: 'Free on all tiers',      monthly_t1: 0,    description: 'Commodity connections — generic webhook, public OAuth, no deep schema mapping.' },
  light:    { label: 'Light integration',      monthly_t1: 10,   description: 'Read-mostly, OAuth install, lightweight.' },
  standard: { label: 'Standard integration',   monthly_t1: 25,   description: 'Read/write with moderate schema mapping.' },
  deep:     { label: 'Deep integration',       monthly_t1: 75,   description: 'Complex schema, certified / compliance-adjacent, support burden.' },
  marquee:  { label: 'Marquee integration',    monthly_t1: null, description: 'Enterprise-only, revenue-critical. Contact us for pricing.' },
};

/** Given a price band and a tier, returns the final monthly price (or null for marquee/custom). */
export function priceForBand(band: IntegrationBand, tier: TierId = 'solo'): number | null {
  const base = INTEGRATION_BANDS[band].monthly_t1;
  if (base === null) return null;
  if (base === 0) return 0;
  const discount = (TIERS.find(t => t.id === tier)?.marketplace_discount_pct ?? 0) / 100;
  return Math.round(base * (1 - discount) * 100) / 100;
}

// ─────────────────────────────────────────────────────────────────────────
// Promo codes
// ─────────────────────────────────────────────────────────────────────────

export type PromoTrack = 'cohort_free' | 'paid_partner' | 'waitlist_deposit';

export interface PromoCode {
  code: string;
  track: PromoTrack;
  label: string;
  description: string;
  beta_terms_required: boolean;
  /** Which tier access this grants */
  grants_tier: TierId;
  /** Human-readable access duration (shown to users) */
  access_duration: string;
  /**
   * Machine-readable duration in days. Signup route uses this to set
   * accounts.plan_expires_at. null = no automatic expiry (ongoing partner,
   * or code doesn't activate a plan by itself — e.g. EARLY-* which waits
   * for Stripe checkout, WAITLIST10 which is applied on first invoice).
   */
  access_duration_days: number | null;
  /**
   * If true, signup activates the plan immediately (no payment needed).
   * Cohort codes = true. Quickstart prepay codes = false (require Stripe).
   * Partner ongoing / waitlist = false (audit-only for now).
   */
  activates_on_signup: boolean;
}

export const PROMO_CODES: PromoCode[] = [
  // Track 1 — Free cohort (training-data consent REQUIRED, activates immediately)
  { code: 'rice-a26', track: 'cohort_free', label: 'RICE Alpha',          description: 'Free Solo for 4 weeks. Training-data consent required.', beta_terms_required: true, grants_tier: 'solo', access_duration: '4 weeks (alpha)', access_duration_days: 28, activates_on_signup: true },
  { code: 'rice-b26', track: 'cohort_free', label: 'RICE Beta',           description: 'Free Solo for 8 weeks. Training-data consent required.', beta_terms_required: true, grants_tier: 'solo', access_duration: '8 weeks (beta)',  access_duration_days: 56, activates_on_signup: true },
  { code: 'bhb-a26',  track: 'cohort_free', label: 'Black Heights Alpha', description: 'Free Solo for 4 weeks. Training-data consent required.', beta_terms_required: true, grants_tier: 'solo', access_duration: '4 weeks (alpha)', access_duration_days: 28, activates_on_signup: true },
  { code: 'bhb-b26',  track: 'cohort_free', label: 'Black Heights Beta',  description: 'Free Solo for 8 weeks. Training-data consent required.', beta_terms_required: true, grants_tier: 'solo', access_duration: '8 weeks (beta)',  access_duration_days: 56, activates_on_signup: true },
  { code: 'xc-a26',   track: 'cohort_free', label: 'Xacution Club Alpha', description: 'Free Solo for 4 weeks. Training-data consent required.', beta_terms_required: true, grants_tier: 'solo', access_duration: '4 weeks (alpha)', access_duration_days: 28, activates_on_signup: true },
  { code: 'xc-b26',   track: 'cohort_free', label: 'Xacution Club Beta',  description: 'Free Solo for 8 weeks. Training-data consent required.', beta_terms_required: true, grants_tier: 'solo', access_duration: '8 weeks (beta)',  access_duration_days: 56, activates_on_signup: true },
  { code: 'ca90-a26', track: 'cohort_free', label: 'CA90 Alpha',          description: 'Free Solo for 4 weeks. Training-data consent required.', beta_terms_required: true, grants_tier: 'solo', access_duration: '4 weeks (alpha)', access_duration_days: 28, activates_on_signup: true },

  // Track 2 — Paid partner / waitlist (audit-only at signup; activation via Stripe or manual)
  { code: 'WAITLIST10', track: 'waitlist_deposit', label: 'Waitlist $10 Deposit', description: '$10 one-time — guaranteed early access slot, applied to first month.', beta_terms_required: false, grants_tier: 'solo', access_duration: 'Applied on first paid invoice', access_duration_days: null, activates_on_signup: false },

  // Quickstart — 3-month prepay at a discount. Code naming per Brandon 2026-04-17:
  // EARLY-$NNN where NNN = the one-time total they prepay. activates_on_signup=false —
  // Phase 2 wires Stripe Checkout; the webhook flips the plan once payment lands.
  { code: 'EARLY-$150',  track: 'paid_partner', label: 'Solo Quickstart', description: '$150 one-time = 3 months of Solo (normally $150/mo) — 3 months for the price of 1. Private.',        beta_terms_required: false, grants_tier: 'solo', access_duration: '3 months', access_duration_days: 90, activates_on_signup: false },
  { code: 'EARLY-$1050', track: 'paid_partner', label: 'Pro Quickstart',  description: '$1050 one-time = 3 months of Pro (normally $1050/mo) — 3 months for the price of 1. Private.',       beta_terms_required: false, grants_tier: 'pro',  access_duration: '3 months', access_duration_days: 90, activates_on_signup: false },
  { code: 'EARLY-$1500', track: 'paid_partner', label: 'Team Quickstart', description: '$1500 one-time = 3 months of Team, 2 seats (normally $1500/mo) — 3 months for the price of 1. Private.', beta_terms_required: false, grants_tier: 'team', access_duration: '3 months', access_duration_days: 90, activates_on_signup: false },

  // Ongoing discounts (kept for partners who commit month-to-month instead of prepay). Audit-only at signup.
  { code: 'PARTNER25',  track: 'paid_partner', label: 'Development Partner', description: '$25/mo ongoing Solo (50% off) while we are in development. Private.', beta_terms_required: false, grants_tier: 'solo', access_duration: 'Ongoing while phase is open', access_duration_days: null, activates_on_signup: false },
];

/**
 * Maps the user-facing tier (from PromoCode.grants_tier) to the value we
 * store in accounts.plan. Compresses solo/pro/team back to the legacy
 * starter/professional CHECK values because Turso/libsql cannot rewrite
 * the CHECK constraint on the live DB — see
 * docs/audits/2026-05-12-v116-postmortem.md (workspace-main) for the
 * five crashes that established this. v116 shipped as a stub.
 *
 * Reverse mapping (stored → exposed v3 tier name) is in the application
 * layer per the follow-up spec
 * docs/superpowers/specs/2026-05-12-app-layer-tier-mapping-design.md.
 */
export const TIER_TO_STORED_PLAN: Record<TierId, 'free' | 'starter' | 'professional' | 'enterprise'> = {
  free:       'free',
  solo:       'starter',
  pro:        'professional',
  team:       'professional',
  enterprise: 'enterprise',
};

/**
 * Quickstart 3-month prepay offers surfaced prominently on the pricing page.
 * Discount vs regular monthly price is baked into each record.
 */
export interface QuickstartOffer {
  tier_id: TierId;
  promo_code: string;
  monthly_retail: number;
  monthly_discounted: number;
  total_upfront: number;
  seats_included: number | string;
  percent_off: number;
  note?: string;
}

/**
 * Feature flag — hides the Quickstart section on /pricing until Stripe
 * products are wired in workspace-main. Defaults to false so a fresh deploy
 * doesn't accidentally expose paid buttons that still create free accounts.
 *
 * Flip via env: NEXT_PUBLIC_SHOW_QUICKSTART=true on the Vercel project.
 */
export const QUICKSTART_ENABLED =
  process.env.NEXT_PUBLIC_SHOW_QUICKSTART === 'true';

/**
 * Gate: when true, account creation requires a valid invitation / promo code
 * (cohort like rice-a26, Quickstart like EARLY-$150, etc). Applies BOTH to the
 * client-side form (required field, block submit) AND to the server-side
 * /api/auth/signup route (rejects missing or invalid code).
 *
 * Use to lock down signups during cohort launches so the only paths in are
 * a handed-out code or a paid Quickstart prepay — no walk-up free signups.
 *
 * Flip via env: NEXT_PUBLIC_SIGNUP_REQUIRES_PROMO=true on Vercel (client-side
 * UX) + SIGNUP_REQUIRES_PROMO=true on Vercel (server-side enforcement). Set
 * both so bypassing the form via curl still gets rejected.
 */
export const SIGNUP_REQUIRES_PROMO =
  process.env.NEXT_PUBLIC_SIGNUP_REQUIRES_PROMO === 'true';

export const QUICKSTART_OFFERS: QuickstartOffer[] = [
  { tier_id: 'solo', promo_code: 'EARLY-$150',  monthly_retail: 150,  monthly_discounted: 50,  total_upfront: 150,  seats_included: 1,   percent_off: 67 },
  { tier_id: 'pro',  promo_code: 'EARLY-$1050', monthly_retail: 1050, monthly_discounted: 350, total_upfront: 1050, seats_included: 1,   percent_off: 67 },
  { tier_id: 'team', promo_code: 'EARLY-$1500', monthly_retail: 1500, monthly_discounted: 500, total_upfront: 1500, seats_included: 2,   percent_off: 67, note: '$185/seat applies for additional seats at regular rate during the promo window.' },
];

export function getPromoCode(code: string): PromoCode | undefined {
  return PROMO_CODES.find(p => p.code.toLowerCase() === code.toLowerCase());
}

// ─────────────────────────────────────────────────────────────────────────
// Machine-readable pricing — the SINGLE SOURCE OF TRUTH for every price-showing
// surface (/upgrade, /settings/billing, /pricing, /signup, /cu). No surface may
// hardcode a dollar figure; read it from here.
//
// Promo vs normal (confirmed 2026-09-08): the PROMO price applies ONLY on a
// 3-month one-time upfront payment (the Quickstart prepay path). MONTHLY
// recurring billing = the NORMAL price. 3 months at promo == 1 month at normal
// (Solo 3×$50=$150; Pro 3×$350=$1,050; Team 3×$500=$1,500).
// ─────────────────────────────────────────────────────────────────────────

/** Enterprise / sales-led contact target. Enterprise never has self-serve
 *  checkout — the CTA is a mailto. (Receiving mail at this address needs an
 *  inbound route on the sageaaa domain — Brandon's infra item.) */
export const ENTERPRISE_CONTACT_EMAIL = 'sales@sageaaa.com';
export const ENTERPRISE_CONTACT_MAILTO =
  `mailto:${ENTERPRISE_CONTACT_EMAIL}?subject=Enterprise%20inquiry`;

export interface PlatformTierPricing {
  id: Exclude<TierId, 'free'>;   // solo | pro | team | enterprise
  name: string;
  /** DB plan_id used for the monthly Stripe subscribe path. */
  plan_id: string;
  /** Normal monthly recurring price (what /account/billing/subscribe charges).
   *  null = Enterprise (no self-serve). */
  normal_monthly: number | null;
  /** Effective monthly price on the 3-month prepay promo. null = Enterprise. */
  promo_monthly: number | null;
  /** One-time total for the 3-month prepay (== 1 month at normal). */
  promo_total_upfront: number | null;
  /**
   * The EARLY-$ Quickstart code the 3-month-prepay button posts to
   * /account/billing/quickstart-checkout. Named for the total charged, and the
   * total charged IS promo_total_upfront: since 2026-09-26 the hub builds the
   * Stripe line item with inline price_data from its own QUICKSTART_CODES
   * (hub src/billing/quickstart-checkout.ts), so no env Stripe price id sits
   * between this number and the charge. tests/unit/pricing-truth.test.ts pins
   * code ↔ amount ↔ tier.
   */
  quickstart_code: string | null;
  seats_included?: number;
  per_seat_addon?: number;
  contact?: boolean;
  summary: string;
  features: string[];
}

export const PLATFORM_PRICING: PlatformTierPricing[] = [
  {
    id: 'solo',
    name: 'Solo',
    plan_id: 'plan-solo',
    normal_monthly: 150,
    promo_monthly: 50,
    promo_total_upfront: 150,
    quickstart_code: 'EARLY-$150',
    seats_included: 1,
    summary: TIERS.find(t => t.id === 'solo')?.summary ?? '',
    features: TIERS.find(t => t.id === 'solo')?.features ?? [],
  },
  {
    id: 'pro',
    name: 'Pro',
    plan_id: 'plan-pro',
    normal_monthly: 1050,
    promo_monthly: 350,
    promo_total_upfront: 1050,
    quickstart_code: 'EARLY-$1050',
    seats_included: 1,
    summary: TIERS.find(t => t.id === 'pro')?.summary ?? '',
    features: TIERS.find(t => t.id === 'pro')?.features ?? [],
  },
  {
    id: 'team',
    name: 'Team',
    plan_id: 'plan-team',
    normal_monthly: 1500,
    promo_monthly: 500,
    promo_total_upfront: 1500,
    quickstart_code: 'EARLY-$1500',
    seats_included: 2,
    per_seat_addon: 185,
    summary: TIERS.find(t => t.id === 'team')?.summary ?? '',
    features: TIERS.find(t => t.id === 'team')?.features ?? [],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    plan_id: 'plan-enterprise',
    normal_monthly: null,
    promo_monthly: null,
    promo_total_upfront: null,
    quickstart_code: null,
    contact: true,
    summary: TIERS.find(t => t.id === 'enterprise')?.summary ?? '',
    features: TIERS.find(t => t.id === 'enterprise')?.features ?? [],
  },
];

/** The self-serve platform tiers (solo/pro/team) — excludes Enterprise. */
export const PLATFORM_SELF_SERVE = PLATFORM_PRICING.filter(t => !t.contact);
export const PLATFORM_ENTERPRISE =
  PLATFORM_PRICING.find(t => t.contact) ?? null;

export function getPlatformTier(id: string): PlatformTierPricing | undefined {
  return PLATFORM_PRICING.find(t => t.id === id);
}

// ── Computer Use — a SEPARATE product from the web platform ──────────────────
// A CU subscription does NOT unlock the web platform (the paywall requires a
// platform-tier plan). Same promo-vs-normal model: promo = 3-month prepay.

export type CuTierId = 'base' | 'starter' | 'pro' | 'enterprise';

/** cu_billing.plan_id / metadata[cu_plan_id] value the CTA buys. null =
 *  Enterprise (no self-serve — mailto instead). */
export type CuPlanId = 'cu-base' | 'cu-starter' | 'cu-pro';

export interface CuTier {
  id: CuTierId;
  name: string;
  /** The tier the /cu CTA posts to /api/billing/cu-checkout ({ tier }).
   *  Same as `id` for self-serve tiers; null for Enterprise. */
  plan_id: CuPlanId | null;
  /** Normal monthly price. null = Enterprise (contact). */
  normal_monthly: number | null;
  /** 3-month-prepay effective monthly. null = Enterprise. */
  promo_monthly: number | null;
  contact?: boolean;
  highlight?: boolean;
  blurb: string;
  features: string[];
}

export const COMPUTER_USE_TIERS: CuTier[] = [
  {
    id: 'base',
    name: 'Base',
    plan_id: 'cu-base',
    normal_monthly: 15,
    promo_monthly: 5,
    blurb: 'For hobbyists and developers testing agent control.',
    features: ['Full computer-use capability', 'Foreground, sidecar & browser modes', 'All safety controls'],
  },
  {
    id: 'starter',
    name: 'Starter',
    plan_id: 'cu-starter',
    normal_monthly: 25,
    promo_monthly: 15,
    blurb: 'For solo operators running real, recurring workflows.',
    features: ['Everything in Base', 'Remove the on-screen brand plate', 'Priority updates'],
  },
  {
    id: 'pro',
    name: 'Pro',
    plan_id: 'cu-pro',
    normal_monthly: 49,
    promo_monthly: 25,
    highlight: true,
    blurb: 'For power users driving heavy, multi-app automation.',
    features: ['Everything in Starter', 'Look & feel customization', 'Higher concurrency', 'Priority support'],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    plan_id: null,
    normal_monthly: null,
    promo_monthly: null,
    contact: true,
    blurb: 'For teams, servers, and unattended fleets.',
    features: ['Headless / data-center execution', 'Multiple concurrent sessions', 'Central management & support'],
  },
];

// ─────────────────────────────────────────────────────────────────────────
// Copy helpers for the public pages
// ─────────────────────────────────────────────────────────────────────────

export const FUNNEL_COPY = {
  free_trial_hook: 'Try CTRL-A right now — 5 free messages on the landing page, no account required.',
  waitlist_pitch:  "We're in early access. Join the waitlist to reserve a Solo/Pro/Team slot, or enter a cohort code to jump in now.",
  beta_consent_label: 'I agree that my usage data during free beta may be used to train and improve CTRL-A agents. No financial or health data is ever shared externally. See the Beta Data Terms.',
} as const;

export const TIER_CONFIG_NOTE =
  'Marketplace discount compounds across tiers. Additional credits available via prepaid top-ups ($10 / $25 / $50 / $100 packs).';
